import {
  APP_UPDATE_POLL_MS,
  APP_UPDATE_RELOAD_COOLDOWN_MS,
  appVersionUrl,
  canAutoReloadAfterDeploy,
  isNewerAppBuild,
  parseAppVersionPayload,
  resolveAppUpdateAction,
  shouldPromptWhenReloadBlocked,
  shouldSkipAppUpdatePoll,
  type DeployReloadCooldownState,
} from '../utils/appUpdate';

const LAST_RELOAD_KEY = 'mp-app-update-last-reload';

type Listener = (available: boolean) => void;

const listeners = new Set<Listener>();
let promptVisible = false;
let memoryLastReloadAtMs: number | null = null;

function setPromptVisible(next: boolean): void {
  if (promptVisible === next) return;
  promptVisible = next;
  listeners.forEach((listener) => listener(promptVisible));
}

export function subscribeAppUpdatePrompt(listener: Listener): () => void {
  listeners.add(listener);
  listener(promptVisible);
  return () => {
    listeners.delete(listener);
  };
}

function readCooldownState(): DeployReloadCooldownState {
  const memory = memoryLastReloadAtMs;
  try {
    const raw = sessionStorage.getItem(LAST_RELOAD_KEY);
    const stored = raw ? Number(raw) : NaN;
    const storedMs = Number.isFinite(stored) ? stored : null;
    const values = [storedMs, memory].filter((value): value is number => value != null);
    return { persisted: true, lastReloadAtMs: values.length > 0 ? Math.max(...values) : null };
  } catch {
    return { persisted: false, lastReloadAtMs: memory };
  }
}

function markReload(nowMs: number): boolean {
  memoryLastReloadAtMs = nowMs;
  try {
    sessionStorage.setItem(LAST_RELOAD_KEY, String(nowMs));
    return true;
  } catch {
    return false;
  }
}

export function reloadToLatestApp(options?: { force?: boolean }): boolean {
  const now = Date.now();
  const force = options?.force === true;
  if (!force && !canAutoReloadAfterDeploy(readCooldownState(), now)) return false;
  if (!markReload(now) && !force) return false;
  window.location.reload();
  return true;
}

/**
 * Detects a new frontend build. Hidden tabs and missing chunks reload;
 * a visible tab shows a prompt instead. Background tabs keep polling so they
 * can reload without waiting for the user to come back. Skipped in Vite dev (HMR).
 */
export function startAppUpdateWatcher(): () => void {
  const currentBuildId = typeof __APP_BUILD_ID__ !== 'undefined' ? String(__APP_BUILD_ID__).trim() : '';
  if (!currentBuildId || import.meta.env.DEV) {
    return () => undefined;
  }

  let disposed = false;
  let inFlight = false;
  let updateAvailable = false;
  let retryTimer: number | null = null;

  const scheduleReloadRetry = () => {
    if (retryTimer != null || disposed) return;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      apply({ brokenChunks: true });
    }, APP_UPDATE_RELOAD_COOLDOWN_MS);
  };

  const apply = (opts?: { brokenChunks?: boolean }) => {
    if (disposed) return;
    const brokenChunks = opts?.brokenChunks === true;
    const action = resolveAppUpdateAction({
      updateAvailable,
      hidden: document.visibilityState !== 'visible',
      brokenChunks,
    });
    if (action === 'reload') {
      if (reloadToLatestApp()) return;
      if (shouldPromptWhenReloadBlocked(action)) setPromptVisible(true);
      if (brokenChunks && readCooldownState().persisted) scheduleReloadRetry();
      return;
    }
    setPromptVisible(action === 'prompt');
  };

  const check = async () => {
    if (disposed || inFlight) return;
    inFlight = true;
    try {
      const res = await fetch(appVersionUrl(Date.now()), { cache: 'no-store' });
      if (!res.ok) return;
      const remote = parseAppVersionPayload(await res.json().catch(() => null));
      if (!remote || !isNewerAppBuild(currentBuildId, remote)) return;
      updateAvailable = true;
      apply();
    } catch {
      // Offline / HTML fallback — keep the current tab.
    } finally {
      inFlight = false;
    }
  };

  const onVisibility = () => {
    if (updateAvailable) {
      apply();
      return;
    }
    void check();
  };

  const onPreloadError = (event: Event) => {
    if (typeof event.preventDefault === 'function') event.preventDefault();
    updateAvailable = true;
    apply({ brokenChunks: true });
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('vite:preloadError', onPreloadError);
  const interval = window.setInterval(() => {
    if (
      shouldSkipAppUpdatePoll({
        hidden: document.visibilityState !== 'visible',
        updateAlreadyKnown: updateAvailable,
      })
    ) {
      return;
    }
    void check();
  }, APP_UPDATE_POLL_MS);

  void check();

  return () => {
    disposed = true;
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.clearInterval(interval);
    if (retryTimer != null) window.clearTimeout(retryTimer);
  };
}
